// L1 (global certified templates) template write hooks. Parallel to
// `l2TemplatesQuery.ts` but targets the layer1 routes — no tenantId
// in the URL, platform_admin role-gated server-side.
//
//   useUploadL1Template()  — multipart upload → DocAI/local extractor
//   useSubmitL1Draft()     — submit fully-typed draft
//   useCertifyL1Template() — flip draft → certified
//
// Reads (the attorney library browser) still go through
// `templatesQuery.ts:useCertifiedTemplates` — that hook is L1
// certified-only by server-side filter and is the right shape for
// browsing.
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useApi } from "./api";
import type {
  CertifiedTemplateRow,
  SubmitDraftResponse,
  SubmitDraftVariables,
} from "./templatesQuery";
import type { UploadL2Result } from "./l2TemplatesQuery";

// The upload result shape is identical between L1 and L2 — the server
// returns { id, draftContent } either way. Re-use the L2 type as the
// canonical shape.
export type UploadL1Result = UploadL2Result;

export type UploadL1Input = {
  file: File;
  // S-216 — when true, request `?autoTag=1` so the response carries an
  // inline tag suggestion (or `tagsError` when tagging fails).
  autoTag?: boolean;
};

export type SubmitL1DraftInput = {
  id: string;
  // S-216 — human-readable title (auto-tagged or manual).
  title?: string | null;
  dealType: CertifiedTemplateRow["dealType"];
  guild: CertifiedTemplateRow["guild"];
  tier: CertifiedTemplateRow["tier"];
  content: string;
  effectiveFrom: string; // ISO datetime
  // S-216 — anchor CBA picked manually or via auto-tag.
  anchorCBA?: string | null;
  // [S-633] Agreement form — carried through so a draft save preserves it.
  documentForm?: CertifiedTemplateRow["documentForm"];
  // [S-802 follow-up] Where the author's edit left each variable, so the tag
  // set is re-anchored in the SAME request that stores the content.
  variables?: SubmitDraftVariables;
};

// S-216 — taxonomy suggestion shape the auto-tag endpoint returns.
// Mirrors TemplateTagSuggestion in @csai/core but kept locally so the
// web bundle doesn't pull in zod for what is plain wire data.
export type TemplateTagSuggestion = {
  title: string;
  dealType: CertifiedTemplateRow["dealType"];
  guild: CertifiedTemplateRow["guild"];
  tier: CertifiedTemplateRow["tier"];
  anchorCBA: string | null;
  confidence: number;
  rationale: {
    title: string;
    dealType: string;
    guild: string;
    tier: string;
    anchorCBA: string;
  };
};

export const L1_TEMPLATES_KEY = ["templates", "L1"] as const;

export function useUploadL1Template(): UseMutationResult<
  UploadL1Result & { tags?: TemplateTagSuggestion; tagsError?: string },
  Error,
  UploadL1Input | File
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: async (input) => {
      const { file, autoTag } =
        input instanceof File ? { file: input, autoTag: false } : input;
      const form = new FormData();
      form.append("file", file);
      const query = autoTag === true ? "?autoTag=1" : "";
      return await api<
        UploadL1Result & { tags?: TemplateTagSuggestion; tagsError?: string }
      >(`/layer1/templates/upload${query}`, {
        method: "POST",
        body: form,
      });
    },
  });
}

export function useSubmitL1Draft(): UseMutationResult<
  SubmitDraftResponse,
  Error,
  SubmitL1DraftInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api<SubmitDraftResponse>("/layer1/templates/draft", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      // Broad "templates" prefix so the admin management list
      // (useAdminTemplates → ["templates","admin",…]) also refetches. [S-211 fix]
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useCertifyL1Template(): UseMutationResult<
  CertifiedTemplateRow,
  Error,
  { templateId: string; effectiveFrom: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, effectiveFrom }) =>
      api<CertifiedTemplateRow>(
        `/layer1/templates/${encodeURIComponent(templateId)}/certify`,
        { method: "POST", body: { effectiveFrom } }
      ),
    onSuccess: () => {
      // Broad "templates" prefix so the admin management list
      // (useAdminTemplates → ["templates","admin",…]) also refetches. [S-211 fix]
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

/**
 * S-216 — POST /layer1/templates/auto-tag. Sends the parsed template
 * content to the admin backend, which calls TemplateAutoTagPort and
 * returns a structured suggestion. 503 surfaces as a mutation error
 * the caller maps to "Auto-tag isn't available — tag manually" copy.
 */
export function useAutoTagL1Template(): UseMutationResult<
  TemplateTagSuggestion,
  Error,
  { content: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ content }) =>
      api<TemplateTagSuggestion>(`/layer1/templates/auto-tag`, {
        method: "POST",
        body: { content },
      }),
  });
}
